import { Api } from "../../Api";
import { AsyncSemaphore } from "../../AsyncSemaphore";
import { DataFrame, Method, Status } from "../../DataFrame";
import { DependencyContainer } from "../../DependencyContainer";
import { ExalusConnectionService } from "../ExalusConnectionService";
import { IExalusConnectionService } from "../IExalusConnectionService";
import { IControllerExtensionsService } from "./IControllerExtensionsService";

export class ControllerExtensionsService implements IControllerExtensionsService {
    public static readonly ServiceName = "StatisticsInfoService";

    private _extensionInfoCache: Map<string, ExtensionInfo> = new Map();
    private _protocolInfoCache: Map<string, ProtocolInfo> = new Map();
    private _isExtensionInfoInitialized: boolean = false;
    private _semaphore: AsyncSemaphore = new AsyncSemaphore(1);


    GetServiceName(): string {
        return ControllerExtensionsService.ServiceName;
    }

    async GetExtensionsInfoAsync(extensionGuid?: string, forceUpdate = false): Promise<ExtensionInfo[]> {
        DependencyContainer.Log?.Debug(ControllerExtensionsService.ServiceName, `Getting ExtensionsInfo, data loaded from cache: ${!forceUpdate}`);

        let lock = await this._semaphore.AcquireAsync();

        try {
            if (!this._isExtensionInfoInitialized || forceUpdate) {
                await this.GetExtensionInfoFromControllerAsync();
            }
        }
        finally {
            lock.Release();
        }

        if ([...this._extensionInfoCache.values()].length == 0) {
            return [];
            //throw new CannotGetExtensions("Cannot get ExtensionsInfo, controler may return empty list of Extensions or other unknown error occurs!");
        }

        if (extensionGuid != null) {
            const extInfo = this._extensionInfoCache.get(extensionGuid);
            if (extInfo == null)
                throw new CannotGetExtensions("Cannot get ExtensionsInfo, extension not found.");

            return [extInfo];
        }
        else
            return [...this._extensionInfoCache.values()];

    }

    async GetProtocolInfoAsync(protocolGuid?: string, forceUpdate = false, retrying = false): Promise<ProtocolInfo[]> {
        DependencyContainer.Log?.Debug(ControllerExtensionsService.ServiceName, `Getting ProtocolInfo, data loaded from cache: ${!forceUpdate}`);

        if (forceUpdate)
            await this.GetProtocolExtensionInfoFromControllerAsync();

        if ([...this._protocolInfoCache.values()].length == 0 && !retrying) {
            await this.GetProtocolInfoAsync(protocolGuid, true, true);
        }
        else if ([...this._protocolInfoCache.values()].length == 0) {
            throw new CannotGetExtensions("Cannot get ProtocolInfo, controler may return empty list of Extensions or other unknown error occurs!");
        }

        if (protocolGuid != null) {
            const extInfo = this._protocolInfoCache.get(protocolGuid);
            if (extInfo == null)
                throw new CannotGetExtensions("Cannot get ProtocolInfo, extension not found.");

            return [extInfo];
        }
        else
            return [...this._protocolInfoCache.values()];

    }

    protected async GetExtensionInfoFromControllerAsync(): Promise<void> {
        const request = new ExtensionInfoRequest();
        const result = await Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName).SendAndWaitForResponseAsync<ExtensionInfo[]>(request, 16000, false);

        if (result == null || result.Status == null)
            throw new CannotGetExtensions(`Cannot get ExtensionsInfo from controller, controller response does not contain data!`);

        if (result.Status != Status.OK)
            throw new CannotGetExtensions(`Cannot get ExtensionsInfo from controller! Response status: ${result.Status}`);

        if (result.Data == null)
            throw new CannotGetExtensions(`Cannot get ExtensionsInfo from controller, controller response does not contain data!`);

        this._extensionInfoCache.clear();
        result.Data.forEach(res => {
            this._extensionInfoCache.set(res.ExtensionGuid, res);
        })

        if ([...this._extensionInfoCache.values()].length != 0)
            this._isExtensionInfoInitialized = true;
    }

    protected async GetProtocolExtensionInfoFromControllerAsync(): Promise<void> {
        const request = new ProtocolInfoRequest();
        const result = await Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName).SendAndWaitForResponseAsync<ProtocolInfo[]>(request, 16000, false);

        if (result == null || result.Status == null)
            throw new CannotGetExtensions(`Cannot get ProtocolInfo from controller, controller response does not contain data!`);

        switch (result.Status) {
            case Status.OK:
                if (result.Data == null)
                    throw new CannotGetExtensions(`Cannot get ProtocolInfo from controller, controller response does not contain data!`);

                this._protocolInfoCache.clear();
                result.Data.forEach(res => {
                    this._protocolInfoCache.set(res.PublicGuid, res);
                })
                break;

            case Status.ResourceDoesNotExists:
                DependencyContainer.Log?.Debug(ControllerExtensionsService.ServiceName, `Cannot get ProtocolInfo from controller, controller software does not implement this yet, update controller to the newest software!`);
                break;

            default:
                throw new CannotGetExtensions(`Cannot get ProtocolInfo from controller! Response status: ${result.Status}`);
        }
    }
}

export class ExtensionInfo {
    ExtensionGuid: string = "";
    APIVersion: string = "";
    ExtensionVersion: string = "";
    Name: string = "";
    PublisherGuid: string = "";
    ExtensionSystemGuid: string = "";
}

export class ProtocolInfo {
    Guid: string = "";
    PublicGuid: string = "";
    Name: string = "";
}

//Requests
class ExtensionInfoRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/controller/extensions/";
        this.Method = Method.Get;
    }
}

class ProtocolInfoRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/controller/protocols/";
        this.Method = Method.Get;
    }
}

//Errors
class CannotGetExtensions extends Error {
    constructor(public message: string) {
        super(message);
        this.name = "CannotGetExtensions";
    }
}
